import * as THREE from 'three';
import {
  ParametresTraversesDoublePoutre,
  StructureTraversesDoublePoutre,
  RangeeTraversesDoublePoutre,
  GeometrieTraverseDoublePoutre,
  TypeMotifTraverseDoublePoutre,
  TypeMateriauTraverseDoublePoutre,
  Point3DDoublePoutre,
  Point2DDoublePoutre,
  DonneesStructureDoublePoutre,
  MotifPersonnaliseDoublePoutre,
  CourbeBezierDoublePoutre
} from '../types';

/**
 * Service de génération 3D des traverses pour la double poutre
 * Inspiré du service traverses principal mais adapté pour la double poutre
 */
export class TraversesDoublePoutre3DService {
  private materiau: THREE.Material;
  private epaisseurCourante: number = 0.02; // Épaisseur par défaut

  constructor() {
    this.materiau = new THREE.MeshLambertMaterial({ 
      color: 0x8B4513, // Couleur bois par défaut
      transparent: false
    });
  }

  /**
   * Génère la géométrie 3D pour Three.js
   * Méthode appelée par le composant Visualisateur3D
   */
  public genererTraverses3D(structure: StructureTraversesDoublePoutre): THREE.Group {
    const groupe = new THREE.Group();
    groupe.name = 'traverses_double_poutre';
    let totalTraverses = 0;
    let traversesAjoutees = 0;

    structure.rangees.forEach((rangee, indexRangee) => {
      rangee.traverses.forEach((traverse, indexTraverse) => {
        totalTraverses++;
        
        if (traverse.points.length > 0 && traverse.indices.length > 0) {
          const mesh = this.creerMeshTraverse(traverse);
          if (mesh) {
            groupe.add(mesh);
            traversesAjoutees++;
          }
        }
      });
    });
    
    // Traverses ajoutées à la scène
    return groupe;
  }

  /**
   * Génère la structure complète des traverses pour la double poutre
   */
  public genererTraverses(
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): StructureTraversesDoublePoutre {
    if (!parametres.active) {
      return this.creerStructureVide(parametres);
    }

    // Mettre à jour le matériau selon le type et la couleur
    const couleurNumerique = typeof parametres.couleur === 'string' 
      ? parseInt(parametres.couleur.replace('#', ''), 16) 
      : parametres.couleur;
    this.mettreAJourMateriau(couleurNumerique);
    
    // Mettre à jour l'épaisseur courante
    this.epaisseurCourante = parametres.epaisseur;

    const rangees: RangeeTraversesDoublePoutre[] = [];
    
    // Générer chaque rangée de traverses
    for (let i = 0; i < parametres.nombreRangees; i++) {
      const hauteur = this.calculerHauteurRangee(i, parametres, donneesBase);
      const rangee = this.genererRangeeTraverses(
        i,
        hauteur,
        parametres,
        donneesBase
      );
      rangees.push(rangee);
    }

    return {
      id: 'structure_traverses_double_poutre',
      parametres,
      rangees
    };
  }

  /**
   * Crée une structure vide
   */
  private creerStructureVide(parametres: ParametresTraversesDoublePoutre): StructureTraversesDoublePoutre {
    return {
      id: 'structure_traverses_double_poutre_vide',
      parametres,
      rangees: []
    };
  }

  /**
   * Met à jour le matériau selon la couleur
   */
  private mettreAJourMateriau(couleur: number): void {
    if (this.materiau instanceof THREE.MeshLambertMaterial) {
      this.materiau.color.setHex(couleur);
    }
  }

  /**
   * Calcule la hauteur d'une rangée de traverses
   */
  private calculerHauteurRangee(
    indexRangee: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): number {
    // Position absolue de la double poutre = hauteur structure principale + position relative dans la double poutre
    const hauteurAbsolueDoublePoutre = donneesBase.hauteurStructurePrincipale + (donneesBase.hauteur * parametres.hauteurPosition);
    
    if (parametres.nombreRangees === 1) {
      return hauteurAbsolueDoublePoutre;
    }
    
    // Répartir les rangées uniformément autour de la position calculée
    const espacementTotal = (parametres.nombreRangees - 1) * parametres.espacementVertical;
    const hauteurDebut = hauteurAbsolueDoublePoutre - espacementTotal / 2;
    
    return hauteurDebut + (indexRangee * parametres.espacementVertical);
  }

  /**
   * Génère une rangée de traverses selon le motif choisi
   */
  private genererRangeeTraverses(
    indexRangee: number,
    hauteur: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): RangeeTraversesDoublePoutre {
    let traverses: GeometrieTraverseDoublePoutre[] = [];

    // Sélection du motif
    switch (parametres.motif) {
      case TypeMotifTraverseDoublePoutre.SIMPLE:
        traverses = this.genererMotifSimple(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverseDoublePoutre.VAGUE_DOUCE:
        traverses = this.genererMotifVague(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverseDoublePoutre.LOSANGE:
        traverses = this.genererMotifLosange(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverseDoublePoutre.PERSONNALISE:
        traverses = this.genererMotifPersonnalise(hauteur, parametres, donneesBase);
        break;
      default:
        traverses = this.genererMotifSimple(hauteur, parametres, donneesBase);
    }

    return {
      id: `rangee_double_poutre_${indexRangee}`,
      hauteur,
      traverses,
      motifApplique: parametres.motif
    };
  }

  /**
   * Génère le motif simple (traverses entre montants adjacents)
   */
  private genererMotifSimple(
    hauteur: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): GeometrieTraverseDoublePoutre[] {
    const traverses: GeometrieTraverseDoublePoutre[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    // Génération du motif simple
    
    if (!positionsMontants || positionsMontants.length < 2) {
      // Pas assez de montants pour générer des traverses
      return traverses;
    }

    // Déterminer le type de forme
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // Créer des traverses entre montants adjacents
    // Début génération des traverses
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % positionsMontants.length];
      
      // Génération traverse entre montants
      
      if (estCirculaire && centre && rayon > 0) {
        // Mode circulaire
        // Générer des points suivant l'arc circulaire
        const pointsArc = this.genererPointsArc(montantActuel, montantSuivant, centre, rayon, hauteur);
        const traverse = this.creerTraverseCourbe(pointsArc, parametres.epaisseur, parametres.materiau);
        
        // Traverse courbe générée
        
        traverses.push({
          id: `traverse_double_poutre_simple_${i}`,
          points: traverse.points,
          indices: traverse.indices,
          normales: traverse.normales,
          uv: traverse.uv
        });
      } else {
        // Mode droite
        // Créer une traverse droite
        const pointA: Point3DDoublePoutre = { x: montantActuel.x, y: hauteur, z: montantActuel.z };
        const pointB: Point3DDoublePoutre = { x: montantSuivant.x, y: hauteur, z: montantSuivant.z };
        // Points de connexion calculés
        
        const traverse = this.creerTraverseDroite(pointA, pointB, hauteur, parametres.epaisseur, parametres.materiau);
        // Traverse droite générée

        traverses.push({
          id: `traverse_double_poutre_simple_${i}`,
          points: traverse.points,
          indices: traverse.indices,
          normales: traverse.normales,
          uv: traverse.uv
        });
      }
      // Traverse ajoutée au tableau
    }

    // Fin génération des traverses
    return traverses;
  }

  /**
   * Génère le motif vague douce
   */
  private genererMotifVague(
    hauteur: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): GeometrieTraverseDoublePoutre[] {
    const traverses: GeometrieTraverseDoublePoutre[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    if (!positionsMontants || positionsMontants.length < 2) {
      // Pas assez de montants pour générer des traverses vague
      return traverses;
    }

    // Amplitude de la vague fixe, identique aux traverses de base
    const amplitudeVague = 0.1; // 10cm d'amplitude fixe

    // Déterminer le type de forme
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // Créer des traverses ondulées entre montants adjacents
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % positionsMontants.length];
      
      let pointsBase: Point3DDoublePoutre[];
      
      if (estCirculaire && centre && rayon > 0) {
        // Générer les points d'arc pour une structure circulaire
        pointsBase = this.genererPointsArc(montantActuel, montantSuivant, centre, rayon, hauteur);
      } else {
        // Générer des points en ligne droite pour les autres formes
        const nombrePoints = 20;
        pointsBase = [];
        for (let j = 0; j <= nombrePoints; j++) {
          const t = j / nombrePoints;
          pointsBase.push({
            x: montantActuel.x + (montantSuivant.x - montantActuel.x) * t,
            y: hauteur,
            z: montantActuel.z + (montantSuivant.z - montantActuel.z) * t
          });
        }
      }
      
      // Appliquer l'effet vague aux points de base (identique aux traverses de base)
      const pointsVague: Point3DDoublePoutre[] = pointsBase.map((point, index) => {
        const t = index / (pointsBase.length - 1);
        // 2 ondulations entre chaque paire de montants adjacents (comme les traverses de base)
        const nombreCycles = 2;
        const offsetVague = Math.sin(t * Math.PI * nombreCycles) * amplitudeVague;
        
        return {
          x: point.x,
          y: point.y + offsetVague,
          z: point.z
        };
      });

      const traverse = this.creerTraverseCourbe(pointsVague, parametres.epaisseur, parametres.materiau);

      traverses.push({
        id: `traverse_vague_double_poutre_${i}`,
        points: traverse.points,
        indices: traverse.indices,
        normales: traverse.normales,
        uv: traverse.uv
      });
    }

    return traverses;
  }

  /**
   * Génère le motif losange
   */
  private genererMotifLosange(
    hauteur: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): GeometrieTraverseDoublePoutre[] {
    const traverses: GeometrieTraverseDoublePoutre[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    if (!positionsMontants || positionsMontants.length < 2) {
      // Pas assez de montants pour générer des losanges
      return this.genererMotifSimple(hauteur, parametres, donneesBase);
    }

    // Hauteur des losanges adaptée à l'espacement vertical
    const hauteurLosange = Math.max(parametres.espacementVertical || 0.4, 0.3);
    
    // 2 losanges entre chaque paire de montants adjacents
    const nombreLosanges = 2;

    // Créer les losanges
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % positionsMontants.length];
      
      // Distance entre les montants
      const distance = Math.sqrt(
        Math.pow(montantSuivant.x - montantActuel.x, 2) + 
        Math.pow(montantSuivant.z - montantActuel.z, 2)
      );
      
      // Largeur de chaque losange
      const largeurLosange = distance / nombreLosanges;
      
      // Créer les losanges
      for (let j = 0; j < nombreLosanges; j++) {
        const debutLosange = j / nombreLosanges;
        const finLosange = (j + 1) / nombreLosanges;
        const milieuLosange = (debutLosange + finLosange) / 2;
        
        // Points du losange
        const pointDebut = {
          x: montantActuel.x + debutLosange * (montantSuivant.x - montantActuel.x),
          y: hauteur,
          z: montantActuel.z + debutLosange * (montantSuivant.z - montantActuel.z)
        };
        
        const pointMilieuHaut = {
          x: montantActuel.x + milieuLosange * (montantSuivant.x - montantActuel.x),
          y: hauteur + hauteurLosange,
          z: montantActuel.z + milieuLosange * (montantSuivant.z - montantActuel.z)
        };
        
        const pointFin = {
          x: montantActuel.x + finLosange * (montantSuivant.x - montantActuel.x),
          y: hauteur,
          z: montantActuel.z + finLosange * (montantSuivant.z - montantActuel.z)
        };
        
        const pointMilieuBas = {
          x: montantActuel.x + milieuLosange * (montantSuivant.x - montantActuel.x),
          y: hauteur - hauteurLosange,
          z: montantActuel.z + milieuLosange * (montantSuivant.z - montantActuel.z)
        };
        
        // Créer les 4 côtés du losange
        const cotes = [
          [pointDebut, pointMilieuHaut],
          [pointMilieuHaut, pointFin],
          [pointFin, pointMilieuBas],
          [pointMilieuBas, pointDebut]
        ];
        
        cotes.forEach((cote, indexCote) => {
          const traverse = this.creerTraverseDroite(cote[0], cote[1], hauteur, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_losange_double_poutre_${i}_${j}_${indexCote}`,
            points: traverse.points,
            indices: traverse.indices,
            normales: traverse.normales,
            uv: traverse.uv
          });
        });
      }
    }

    return traverses;
  }

  /**
   * Génère le motif personnalisé
   */
  private genererMotifPersonnalise(
    hauteur: number,
    parametres: ParametresTraversesDoublePoutre,
    donneesBase: DonneesStructureDoublePoutre
  ): GeometrieTraverseDoublePoutre[] {
    // Implémentation simplifiée - peut être étendue
    return this.genererMotifSimple(hauteur, parametres, donneesBase);
  }

  /**
   * Calcule le centre d'un ensemble de points
   */
  private calculerCentre(positions: Point3DDoublePoutre[]): Point3DDoublePoutre {
    let sumX = 0, sumZ = 0;
    for (const pos of positions) {
      sumX += pos.x;
      sumZ += pos.z;
    }
    return {
      x: sumX / positions.length,
      y: 0,
      z: sumZ / positions.length
    };
  }

  /**
   * Calcule le rayon d'un cercle
   */
  private calculerRayon(centre: Point3DDoublePoutre, point: Point3DDoublePoutre): number {
    const dx = point.x - centre.x;
    const dz = point.z - centre.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  /**
   * Génère des points suivant un arc circulaire
   */
  private genererPointsArc(
    debut: Point3DDoublePoutre,
    fin: Point3DDoublePoutre,
    centre: Point3DDoublePoutre,
    rayon: number,
    hauteur: number
  ): Point3DDoublePoutre[] {
    const points: Point3DDoublePoutre[] = [];
    const nombrePoints = 10; // Résolution de l'arc
    
    // Calculer les angles
    const angleDebut = Math.atan2(debut.z - centre.z, debut.x - centre.x);
    const angleFin = Math.atan2(fin.z - centre.z, fin.x - centre.x);
    
    let deltaAngle = angleFin - angleDebut;
    if (deltaAngle < 0) deltaAngle += 2 * Math.PI;
    if (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
    
    for (let i = 0; i <= nombrePoints; i++) {
      const t = i / nombrePoints;
      const angle = angleDebut + deltaAngle * t;
      
      points.push({
        x: centre.x + rayon * Math.cos(angle),
        y: hauteur,
        z: centre.z + rayon * Math.sin(angle)
      });
    }
    
    return points;
  }

  /**
   * Crée une traverse courbe
   */
  private creerTraverseCourbe(
    points: Point3DDoublePoutre[],
    epaisseur: number,
    materiau: TypeMateriauTraverseDoublePoutre
  ): { points: Float32Array; indices: Uint16Array; normales: Float32Array; uv: Float32Array } {
    // Implémentation simplifiée - génère une géométrie basique
    const vertices: number[] = [];
    const indices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    
    // Convertir les points en vertices
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      vertices.push(point.x, point.y, point.z);
      normals.push(0, 1, 0); // Normal vers le haut
      uvs.push(i / (points.length - 1), 0);
    }
    
    // Créer des indices pour les segments
    for (let i = 0; i < points.length - 1; i++) {
      indices.push(i, i + 1);
    }
    
    return {
      points: new Float32Array(vertices),
      indices: new Uint16Array(indices),
      normales: new Float32Array(normals),
      uv: new Float32Array(uvs)
    };
  }

  /**
   * Crée une traverse droite
   */
  private creerTraverseDroite(
    pointA: Point3DDoublePoutre,
    pointB: Point3DDoublePoutre,
    hauteur: number,
    epaisseur: number,
    materiau: TypeMateriauTraverseDoublePoutre
  ): { points: Float32Array; indices: Uint16Array; normales: Float32Array; uv: Float32Array } {
    const vertices = [
      pointA.x, pointA.y, pointA.z,
      pointB.x, pointB.y, pointB.z
    ];
    
    const indices = [0, 1];
    
    const normals = [
      0, 1, 0,
      0, 1, 0
    ];
    
    const uvs = [0, 0, 1, 0];
    
    return {
      points: new Float32Array(vertices),
      indices: new Uint16Array(indices),
      normales: new Float32Array(normals),
      uv: new Float32Array(uvs)
    };
  }

  /**
   * Crée un mesh Three.js à partir d'une géométrie de traverse
   */
  private creerMeshTraverse(traverse: GeometrieTraverseDoublePoutre): THREE.Mesh | null {
    try {
      const geometrie = new THREE.BufferGeometry();
      
      // Utiliser directement les données Float32Array du service
      geometrie.setAttribute('position', new THREE.BufferAttribute(traverse.points, 3));
      geometrie.setAttribute('normal', new THREE.BufferAttribute(traverse.normales, 3));
      geometrie.setAttribute('uv', new THREE.BufferAttribute(traverse.uv, 2));
      geometrie.setIndex(Array.from(traverse.indices));
      
      // Créer une géométrie de tube pour donner du volume aux traverses
      const points = [];
      for (let i = 0; i < traverse.points.length; i += 3) {
        points.push(new THREE.Vector3(
          traverse.points[i],
          traverse.points[i + 1],
          traverse.points[i + 2]
        ));
      }
      
      if (points.length >= 2) {
        const curve = new THREE.CatmullRomCurve3(points);
        const tubeGeometry = new THREE.TubeGeometry(curve, 8, this.epaisseurCourante, 8, false);
        const mesh = new THREE.Mesh(tubeGeometry, this.materiau);
        mesh.name = traverse.id;
        return mesh;
      }
      
      return null;
    } catch (error) {
      // Erreur création mesh traverse
      return null;
    }
  }

  /**
   * Nettoie les ressources
   */
  public dispose(): void {
    if (this.materiau) {
      this.materiau.dispose();
    }
  }
}